import { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { TextField } from "@mui/material";

import { startMasterDataFetching } from "../../redux/reducers/drmLiveLoadMasterDataSlice";
import { COLUMN_CONFIGS, getColumnEditPermission } from "../../constData/columnDrmt";

export const EditableCell = ({
  value: initialValue,
  row: { index, id: rowId, isSelected, original },
  column: { id, Header },
  updateData,
}) => {
  const [value, setValue] = useState(initialValue);
  const [isEditing, setIsEditing] = useState(false);
  const { selectedMenu } = useSelector((state) => state.selectedMenu);
  const { masterData } = useSelector((state) => state.masterData);

  const dispatch = useDispatch();
  const exitEditTimeoutRef = useRef(null);
  const { allowInlineEdit, allowNewRecordEdit } = getColumnEditPermission(Header, selectedMenu === "bid_maintenance" ? "Bid" : "Daily");
  const isNewOrDuplicate = Boolean(
    original?.status == "-3" || original?.status == "-2" || original?.status != "-1"
  );

  const canEditCurrentRow = isSelected && (isNewOrDuplicate ? allowNewRecordEdit : allowInlineEdit);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    return () => {
      if (exitEditTimeoutRef.current) {
        clearTimeout(exitEditTimeoutRef.current);
      }
    };
  }, []);

  const handleBlur = useCallback(async (newValue) => {
    const finalValue = newValue;

    if (id === "tariffId" || id === "tariffNumber") {
      const tariffData = masterData?.tariffIds?.data || [];
      const matchedTariff = tariffData.find(
        (tariff) =>
          (id === "tariffId" && tariff.tariffCode === newValue) ||
          (id === "tariffNumber" && tariff.tariffId === newValue)
      );
      await updateData(index, id, finalValue);
      await updateData(index, "equipmentType", null);

      if (id === "tariffId" && matchedTariff) {
        await updateData(index,"tariffNumber", matchedTariff?.tariffId ?? value);
        await updateData(index, "equipmentType", null);
      }

      if (id === "tariffNumber") {
        const nextTariffId = matchedTariff?.tariffCode ?? finalValue;
        await updateData(index, "tariffId", nextTariffId);
        await updateData(index, "equipmentType", null);
      }
    } else {
      await updateData(index, id, finalValue);
    }

    if (id === "carrierScac" && finalValue && finalValue !== original?.carrierScac) {
      await dispatch(
        startMasterDataFetching({
          body: { carrier: finalValue },
          masterDataType: "tariffIds",
          onPage: 1,
        })
      );
    }

    setValue(finalValue);

    if (exitEditTimeoutRef.current) {
      clearTimeout(exitEditTimeoutRef.current);
    }

    exitEditTimeoutRef.current = setTimeout(() => {
      if (finalValue !== null && finalValue !== undefined && finalValue !== "") {
        setIsEditing(false);
      }
    }, 5000);
  }, [value, id, masterData?.tariffIds?.data, index, updateData, original?.carrierScac]);

  const handleFetchTariffIds = useCallback(
    async ({ onPage = 1 } = {}) => {
      const carrier = original?.carrierScac;
      if (!carrier) {
        return;
      }
      return dispatch(
        startMasterDataFetching({
          body: { carrier },
          masterDataType: "tariffIds",
          onPage,
        })
      );
    },
    [original?.carrierScac]
  );

  const handleFetchTariffEquipmentMapping = useCallback(
    async ({ onPage = 1 } = {}) => {
      const tariffId = original?.tariffId;
      if (!tariffId) {
        return;
      }
      return dispatch(
        startMasterDataFetching({
          body: [tariffId],
          masterDataType: "tariffEquipmentMapping",
          onPage,
        })
      );
    },
    [original?.tariffId]
  );


  const handleDoubleClick = useCallback(async () => {
    if (!isSelected) return;
    if (!canEditCurrentRow) return;

    if ((Header === "tariffId" || Header === "tariffNumber") && original?.carrierScac) {
      await handleFetchTariffIds();
    }
    if (Header === "equipmentType" && original?.tariffId) {
      await handleFetchTariffEquipmentMapping();
    }
    setIsEditing(true);
  }, [handleFetchTariffIds, handleFetchTariffEquipmentMapping, isSelected, canEditCurrentRow, Header, original?.carrierScac, original?.tariffId]);

  if (
    isEditing &&
    canEditCurrentRow
  ) {
    const config = COLUMN_CONFIGS[Header];
    if (config?.editComponent) {
      const EditComponent = config.editComponent;
      return (
        <EditComponent value={value} onBlur={handleBlur} header={Header} setIsEditing={setIsEditing} />
      );
    }

    return (
      <TextField
        value={value || ""}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => handleBlur(value)}
        onKeyDown={(e) => e.key === "Enter" && handleBlur(value)}
        autoFocus
        fullWidth
        size="small"
        data-testid={`${Header}-${value}`}
      />
    );
  }
  return (
    <div data-testid={`${Header}-${value}`} onDoubleClick={handleDoubleClick}>
      {value ?? "-"}
    </div>
  );
};
